import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import AnimatedPage from '../components/shared/AnimatedPage';
import { confirmEmailChange } from '../lib/functions';

type State = 'loading' | 'success' | 'error';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<State>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMsg('Missing confirmation token.');
      setState('error');
      return;
    }
    confirmEmailChange({ token })
      .then(() => setState('success'))
      .catch((err: unknown) => {
        const fbErr = err as { code?: string; message?: string };
        if (fbErr.code === 'functions/not-found') {
          setErrorMsg('This confirmation link is invalid or has already been used.');
        } else if (fbErr.code === 'functions/failed-precondition') {
          setErrorMsg('This confirmation link has expired. Please request a new one from Account Settings.');
        } else {
          setErrorMsg(fbErr.message ?? 'Something went wrong. Please try again.');
        }
        setState('error');
      });
  }, [token]);

  return (
    <AnimatedPage>
      <div className="mx-auto max-w-md space-y-6 px-4 py-20 text-center">
        {state === 'loading' && (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
            <p className="text-sm text-gray-500">Confirming your new email…</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl">
              ✓
            </div>
            <h1 className="text-xl font-bold text-gray-900">Email updated!</h1>
            <p className="text-sm text-gray-500">
              Your email address has been changed successfully. Use your new email to sign in from now on.
            </p>
            <Link
              to="/account"
              className="inline-block rounded-xl bg-brand-600 px-6 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Back to Account Settings
            </Link>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-3xl">
              ✕
            </div>
            <h1 className="text-xl font-bold text-gray-900">Confirmation failed</h1>
            <p className="text-sm text-gray-500">{errorMsg}</p>
            <Link
              to="/account"
              className="inline-block rounded-xl bg-brand-600 px-6 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Back to Account Settings
            </Link>
          </>
        )}
      </div>
    </AnimatedPage>
  );
}
